<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { ComponentPublicInstance } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import type { Teach } from '@pocketprep/types'
import { instructorModule } from '@/store/instructor/module'
import { organizationModule } from '@/store/organization/module'
import { userModule } from '@/store/user/module'
import { analyticsModule } from '@/store/analytics/module'
import { isSupportWindow, setCookie } from '@/utils'
import { getSessionToken } from '@/store/ParseUtils'

const { Icon } = UIKit

const route = useRoute()
const router = useRouter()

const showUserDropdown = ref(false)
const showHelpDropdown = ref(false)
const userDropdownBtn = ref<HTMLElement | null>(null)
const helpDropdownBtn = ref<ComponentPublicInstance | null>(null)
const userDropdown = ref<HTMLElement | null>(null)
const helpDropdown = ref<HTMLElement | null>(null)

const currentUser = computed(() => userModule.state.user)

const currentInstructor = computed(() => instructorModule.getters.getCurrentOrgInstructor())

const currentUserOrgInstructors = computed(() => {
    return instructorModule.getters.getCurrentUserOrgInstructors()
        .filter(i => !getOrgInstructorOrganization(i)?.archivedAt)
})

const currentOrgInstructorColors = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructorColors()
})

const organizations = computed(() => {
    return organizationModule.getters.getOrganizations()
})

const currentOrg = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const userInitials = computed(() => {
    return currentUser.value?.firstName && currentUser.value?.lastName
        ? `${currentUser.value.firstName[0]}${currentUser.value.lastName[0]}`.toUpperCase()
        : 'PP'
})

const userColors = computed(() => {
    const colors = currentInstructor.value && currentOrgInstructorColors.value[currentInstructor.value.objectId]
    return colors || currentOrgInstructorColors.value.default
})

const getOrgInstructorOrganization = (orgInstructor: Teach.Class.OrgInstructorJSON) => {
    return organizations.value[orgInstructor.organization.objectId]
}

const clickListener = (e: MouseEvent) => {
    if (showUserDropdown.value) {
        const targetEl = e.target as HTMLElement
        const dropdownEl = userDropdown.value
        const dropdownBtnEl = userDropdownBtn.value
        if (!dropdownBtnEl?.contains(targetEl) && !dropdownEl?.contains(targetEl)) {
            showUserDropdown.value = false
        }
    }

    if (showHelpDropdown.value) {
        const targetEl = e.target as HTMLElement
        const dropdownEl = helpDropdown.value
        const dropdownBtnEl = helpDropdownBtn.value
        if (!dropdownBtnEl?.$el?.contains(targetEl) && !dropdownEl?.contains(targetEl)) {
            showHelpDropdown.value = false
        }
    }
}

const clickSignOut = async () => {
    await userModule.actions.signOut()
    router.push({ name: 'sign-in' })
}

const switchCurrentOrganization = async (orgInstructor: Teach.Class.OrgInstructorJSON) => {
    showUserDropdown.value = false
    const organization = orgInstructor.organization as Teach.Class.OrganizationJSON // From .include('organization')

    // Ensure that route doesn't refer to old org data
    router.push('/')
    await userModule.actions.updateCurrentOrganization(organization)
    window.location.reload()
}

const clickUserDropdownBtn = () => {
    showUserDropdown.value = !showUserDropdown.value
    nextTick(() => {
        userDropdown.value?.focus()
    })
}

const clickHelpDropdownBtn = () => {
    showHelpDropdown.value = !showHelpDropdown.value
    nextTick(() => {
        helpDropdown.value?.focus()
    })
}

const headerTabClicked = (tabName: 'Stats' | 'Questions' | 'Assignments' | 'Admin') => {
    analyticsModule.actions.amplitudeTrack('Tab Clicked', {
        tabContext: 'Header',
        tabName,
    })
}

const clickSwitchToStudy = async () => {
    const studyURL = import.meta.env.VUE_APP_STUDY_URL
    const sessionCookieName = import.meta.env.VUE_APP_SESSION_COOKIE_NAME
    const sessionToken = getSessionToken()

    if (studyURL && sessionToken && sessionCookieName) {
        setCookie({ name: sessionCookieName, value: sessionToken })
        await userModule.actions.updateWebConfig({ lastActiveApp: 'study' })
        await userModule.actions.trackEvent('App_Switch')
        window.location.href = studyURL
    }
}

const showIntercom = () => {
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        showHelpDropdown.value = false
        window.Intercom('show')
    }
}

onMounted(async () => {
    await Promise.all([
        instructorModule.actions.fetchCurrentOrgInstructor(),
        organizationModule.actions.fetchOrganizations(),
        organizationModule.actions.fetchCurrentOrganization(),
        instructorModule.actions.fetchCurrentUserOrgInstructors(),
    ])

    document.addEventListener('click', clickListener)
})

onBeforeUnmount(() => {
    document.removeEventListener('click', clickListener)
})
</script>

<template>
    <header class="head">
        <div class="head__title">
            <img
                class="head__logo"
                src="@/assets/images/pocket-prep-logo.svg"
                alt="Pocket Prep"
            >
            <div>
                Instructor Dashboard
            </div>
        </div>
        <div v-if="currentUser && currentOrg" class="head__navigation">
            <RouterLink
                :to="(!route.fullPath.startsWith('/stats'))
                    ? { name: 'stats' } : ''"
                @mousedown.prevent
                @click="headerTabClicked('Stats')"
            >
                Stats
            </RouterLink>
            <RouterLink
                :to="(!route.fullPath.startsWith('/content'))
                    ? { name: 'content' } : ''"
                @mousedown.prevent
                @click="headerTabClicked('Questions')"
            >
                Questions
            </RouterLink>
            <RouterLink
                :to="(!route.fullPath.startsWith('/assignments'))
                    ? { name: 'assignments' } : ''"
                @mousedown.prevent
                @click="headerTabClicked('Assignments')"
            >
                Assignments
            </RouterLink>
            <RouterLink
                :to="(!route.fullPath.startsWith('/admin'))
                    ? { name: 'admin' } : ''"
                @mousedown.prevent
                @click="headerTabClicked('Admin')"
            >
                Admin
            </RouterLink>
        </div>
        <div v-if="currentUser" class="head__right">
            <div
                ref="userDropdownBtn"
                class="head__user"
                tabindex="0"
                @mousedown.prevent
                @click="clickUserDropdownBtn"
                @keyup.enter="clickUserDropdownBtn"
            >
                <span class="head__user-name">
                    {{ currentUser.firstName || currentUser.username }}
                </span>
                <span v-if="currentOrg" class="head__user-middot">
                    ·
                </span>
                <span v-if="currentOrg" class="head__user-org">
                    {{ currentOrg.name }}
                </span>
                <Icon
                    v-if="currentOrg"
                    class="head__user-dropdown-arrow"
                    :class="{
                        'head__user-dropdown-arrow--up': showUserDropdown
                    }"
                    type="accordionArrow"
                />
            </div>
            <Icon
                v-if="currentOrg"
                ref="helpDropdownBtn"
                class="head__help-icon"
                type="help"
                tabindex="0"
                @mousedown.prevent
                @click="clickHelpDropdownBtn"
                @keyup.enter="clickHelpDropdownBtn"
            />
            <div
                v-if="showUserDropdown"
                ref="userDropdown"
                class="head__user-dropdown"
                tabindex="0"
                @keyup.esc="clickUserDropdownBtn"
            >
                <div class="head__user-info">
                    <div
                        class="head__user-initials"
                        :style="{ backgroundColor: userColors?.fillColor, color: userColors?.textColor }"
                    >
                        {{ userInitials }}
                    </div>
                    <div class="head__user-full-name" :style="{ color: userColors?.textColor }">
                        {{ currentUser.firstName }} {{ currentUser.lastName }}
                    </div>
                </div>
                <div
                    v-for="orgInstructor in currentUserOrgInstructors"
                    :key="orgInstructor.objectId"
                    class="head__user-dropdown-items head__org-info"
                    tabindex="0"
                    @mousedown.prevent
                    @click="switchCurrentOrganization(orgInstructor)"
                >
                    <div class="head__org-name">
                        {{ getOrgInstructorOrganization(orgInstructor)?.name }}
                    </div>
                    <div class="head__org-role">
                        {{ orgInstructor.isAdmin ? 'Admin' : 'Instructor' }}
                    </div>
                </div>
                <div
                    class="head__user-dropdown-items head__switch-app-btn"
                    tabindex="0"
                    @click="clickSwitchToStudy"
                    @keydown.enter="clickSwitchToStudy"
                >
                    Go to Exam Prep
                </div>
                <div
                    class="head__user-dropdown-items head__sign-out-btn"
                    tabindex="0"
                    @click="clickSignOut"
                    @keydown.enter="clickSignOut"
                >
                    Sign Out
                </div>
            </div>
            <div
                v-if="showHelpDropdown"
                ref="helpDropdown"
                class="head__help-dropdown"
                tabindex="0"
            >
                <div
                    class="head__help-dropdown-option"
                    tabindex="0"
                    @mousedown.prevent
                    @click="showIntercom"
                    @keydown.enter="showIntercom"
                >
                    Help Chat
                </div>
                <a
                    class="head__help-dropdown-option"
                    href="https://help.educators.pocketprep.com"
                    target="_blank"
                    @mousedown.prevent
                >
                    Help Center
                </a>
            </div>
        </div>
    </header>
</template>

<style lang="scss" scoped>
@use 'sass:color';

.head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: $white;
    user-select: none;

    &__title {
        display: flex;
        align-items: center;
        font-size: 18px;
        font-weight: 600;
        white-space: nowrap;
    }

    &__logo {
        margin-right: 12px;
    }

    &__navigation {
        display: flex;
        align-items: center;

        a {
            padding: 0 8px;
            margin: 0 2px;
            display: flex;
            justify-content: center;
            align-items: center;
            position: relative;
            height: 42px;
            color: $white;
            text-decoration: none;
            letter-spacing: 0.3px;
            opacity: 0.8;
            outline: none;
            border: 1px solid transparent;
            border-radius: 2px;
            font-weight: 600;

            &:hover {
                opacity: 1;
            }

            &:focus {
                border-color: $sky-blue;
            }

            &.router-link-active {
                opacity: 1;
                color: #ffedcb;

                &::after {
                    content: '';
                    position: absolute;
                    bottom: -6px;
                    left: 0;
                    width: 100%;
                    height: 4px;
                    background: #ffce74;
                }
            }
        }
    }

    &__right {
        position: relative;
        display: flex;
        align-items: center;
    }

    &__user {
        padding: 11px;
        font-size: 15px;
        letter-spacing: 0.1px;
        border: 1px solid transparent;
        border-radius: 2px;
        outline: none;
        cursor: pointer;

        &:hover {
            background-color: rgba($white, 0.15);

            .head__user-dropdown-arrow {
                color: #ffce74;
            }
        }

        &:focus {
            border-color: $sky-blue;
        }
    }

    &__user-name {
        font-weight: 500;
    }

    &__user-middot {
        color: #ffedcb;
        font-weight: 600;
        margin: 0 4px;
    }

    &__user-org {
        font-weight: 500;
        color: rgba($white, 0.85);
    }

    &__user-dropdown-arrow {
        margin-left: 2px;
        transform: scale(0.75);

        &--up {
            transform: scale(0.75) rotate(180deg);
        }
    }

    &__help-icon {
        box-sizing: border-box;
        width: 36px;
        height: 42px;
        padding: 8px;
        margin-right: -8px;
        opacity: 0.8;
        outline: none;
        border: 1px solid transparent;
        border-radius: 2px;
        cursor: pointer;

        &:hover {
            opacity: 1;
        }

        &:focus {
            border-color: $sky-blue;
        }
    }

    &__user-dropdown {
        position: absolute;
        top: 48px;
        width: 220px;
        background-color: white;
        border-radius: 0 0 8px 8px;
        box-shadow: 0 6px 22px 0 rgba($brand-black, 0.2);
        color: $brand-black;
        outline: none;
    }

    &__user-info {
        padding: 30px 0 18px 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        border-bottom: 1px solid $gray-divider;
        margin-bottom: 4px;
    }

    &__user-initials {
        box-sizing: border-box;
        width: 50px;
        height: 50px;
        display: flex;
        justify-content: center;
        font-size: 18px;
        background-color: $beach-sand;
        color: #6d4705;
        border-radius: 100%;
        padding-top: 17px;
        margin-bottom: 5px;
    }

    &__user-full-name {
        line-height: 30px;
        color: #6d4705;
        font-weight: 600;
    }

    &__user-dropdown-items {
        position: relative;
        outline: none;
        cursor: pointer;

        &:hover {
            background-color: color.mix($barely-background, $brand-black, 96%);
        }

        &:focus {
            &::after {
                content: '';
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                border: 1px solid $brand-blue;
                border-radius: 6px;
            }
        }
    }

    &__org-info {
        padding-top: 4px;
        padding-left: 16px;
        padding-bottom: 8px;
    }

    &__org-name {
        letter-spacing: 0.1px;
        font-size: 14px;
        line-height: 20px;
        font-weight: 600;
    }

    &__org-role {
        margin-top: -2px;
        letter-spacing: 0.09px;
        font-size: 13px;
        line-height: 16px;
        color: $slate-03;
    }

    &__switch-app-btn {
        padding: 12px 81px 12px 16px;
        font-size: 14px;
        line-height: 17px;
        font-weight: 500;
        color: $brand-black;
        border-bottom: 1px solid $gray-divider;
        border-top: 1px solid $gray-divider;
    }

    &__sign-out-btn {
        padding: 12px 81px 12px 16px;
        font-size: 14px;
        line-height: 17px;
        font-weight: 500;
        color: $brand-black;
    }

    &__help-dropdown {
        position: absolute;
        top: 48px;
        right: -44px;
        width: 204px;
        background-color: white;
        border-radius: 0 0 8px 8px;
        box-shadow: 0 6px 22px 0 rgba($brand-black, 0.1);
        color: $brand-black;
        outline: none;
    }

    &__help-dropdown-option {
        display: block;
        position: relative;
        letter-spacing: 0.1px;
        padding: 6px 0 6px 24px;
        font-size: 14px;
        line-height: 30px;
        outline: none;
        cursor: pointer;
        color: $brand-black;
        text-decoration: none;

        &:not(:last-child) {
            border-bottom: 1px solid $gray-divider;
        }

        &:hover {
            background-color: color.mix($barely-background, $brand-black, 96%);
        }

        &:focus {
            &::after {
                content: '';
                position: absolute;
                top: 4px;
                bottom: 4px;
                left: 11px;
                right: 10px;
                border: 1px solid $brand-blue;
            }
        }
    }
}
</style>
